import { maskPhone } from '../utils/maskUtils';
import React, { useState, useEffect } from 'react';
import { getAppointments, updateAppointmentStatus, createAppointment, getClinicConfig } from '../services/storageService';
import { Appointment, ClinicConfig, Attendance } from '../types';
import { formatDateBr, generateTimeSlots } from '../utils/dateUtils';
import { attendanceLabel, getAvailableAttendance, todaySP } from '../utils/schedulingRules';
import { useToast } from '../components/Toast';
import { Calendar, momentLocalizer, Views, View } from 'react-big-calendar';
import moment from 'moment';
import { AppointmentOverview } from '../components/AppointmentOverview';
import { Modal } from '../components/Modal';
import { Button } from '../components/Button';
import { AppointmentList } from '../components/AppointmentList';
import { AppointmentDetails } from '../components/AppointmentDetails';
import 'moment/locale/pt-br';
import 'react-big-calendar/lib/css/react-big-calendar.css';

// Setup the localizer for react-big-calendar
moment.locale('pt-br');
const localizer = momentLocalizer(moment);

export type AppointmentSection = 'pending' | 'upcoming' | 'calendar' | 'history' | 'requests';
export const AdminAppointments: React.FC<{ section?: AppointmentSection; onNavigate?: (section: AppointmentSection) => void }> = ({ section, onNavigate }) => {
  const { addToast } = useToast();
  const [calendarView, setCalendarView] = useState<View>(Views.MONTH);
  const [calendarDate, setCalendarDate] = useState(new Date());
  const navigate = (next: AppointmentSection) => { setActiveTab(next); onNavigate?.(next); };
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [config, setConfig] = useState<ClinicConfig | null>(null);
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
  const [loadError, setLoadError] = useState(false);

  // Persist Active Tab
  const [activeTab, setActiveTab] = useState<'pending' | 'upcoming' | 'calendar' | 'history' | 'requests'>(() => {
    return (localStorage.getItem('admin_appointments_tab') as 'pending' | 'upcoming' | 'calendar' | 'history' | 'requests') || 'pending';
  });
  useEffect(() => { if (section) setActiveTab(section); }, [section]);

  const [loading, setLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  // Filters - Default Date to Today
  const todayStr = todaySP();
  const [filterName, setFilterName] = useState('');
  const [filterDate, setFilterDate] = useState(todayStr);
  const [filterStatus, setFilterStatus] = useState<'all' | 'confirmed' | 'cancelled'>('all');

  // Pagination for history
  const [historyPage, setHistoryPage] = useState(1);
  const itemsPerPage = 12; // Increased slightly for compact view

  // Cancellation Modal State
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [appointmentToCancel, setAppointmentToCancel] = useState<string | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [isCancelling, setIsCancelling] = useState(false);
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const [appointmentToConfirm, setAppointmentToConfirm] = useState<Appointment | null>(null);
  const [confirmError, setConfirmError] = useState('');

  // Creation Modal State
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [newAppt, setNewAppt] = useState({
    clientName: '',
    clientPhone: '',
    date: '',
    time: '',
    notes: ''
    , attendance: '' as Attendance | ''
  });
  const [isCreating, setIsCreating] = useState(false);

  // Save Tab State
  useEffect(() => {
    localStorage.setItem('admin_appointments_tab', activeTab);
  }, [activeTab]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setLoadError(false);
      try {
        const [appts, cfg] = await Promise.all([getAppointments(), getClinicConfig()]);
        setAppointments(appts);
        setConfig(cfg);
      } catch (error) {
        console.error("Erro ao buscar agendamentos", error);
        addToast("Erro ao carregar agendamentos", "error");
        setLoadError(true);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [refreshKey, addToast]);

  const handleConfirm = async (id: string) => {
    if (confirmingId) return false;
    setConfirmingId(id);
    try {
      await updateAppointmentStatus(id, 'confirmed');
      setRefreshKey(p => p + 1);
      addToast("Agendamento confirmado!", "success");
      return true;
    } catch (err) {
      addToast("Erro ao confirmar agendamento.", "error");
      return false;
    } finally { setConfirmingId(null); }
  };

  const requestConfirmation = (id: string) => {
    const appointment = appointments.find(item => item.id === id && item.status === 'pending');
    if (!appointment || confirmingId) return;
    setConfirmError('');
    setAppointmentToConfirm(appointment);
  };

  const submitConfirmation = async () => {
    if (!appointmentToConfirm || confirmingId) return;
    setConfirmError('');
    if (await handleConfirm(appointmentToConfirm.id)) {
      setAppointmentToConfirm(null);
      setSelectedAppointment(null);
    } else setConfirmError('Não foi possível confirmar a sessão. Tente novamente.');
  };

  const openCancelModal = (id: string) => {
    setAppointmentToCancel(id);
    setCancelReason('');
    setIsCancelModalOpen(true);
  };

  const handleCancelSubmit = async () => {
    if (!appointmentToCancel || isCancelling) return;
    setIsCancelling(true);
    try {
      const reason = cancelReason || 'Cancelado pelo administrador';
      await updateAppointmentStatus(appointmentToCancel, 'cancelled', reason);
      setIsCancelModalOpen(false);
      setRefreshKey(p => p + 1);
      addToast("Agendamento cancelado com sucesso.", "info");
    } catch (err) {
      addToast("Erro ao cancelar agendamento.", "error");
    } finally { setIsCancelling(false); }
  };

  const handleCreateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newAppt.clientName || !newAppt.clientPhone || !newAppt.date || !newAppt.time) {
      addToast("Preencha os campos obrigatórios.", "error");
      return;
    }

    setIsCreating(true);
    try {
      // The backend creates it as 'pending', but since admin is creating, we might want to automatically confirm it?
      // For now, we'll create it and then confirm it immediately, or we can just let it be pending and admin confirms.
      // Easiest is to create, then immediately update to confirmed.
      if (!newAppt.attendance) { addToast('Selecione Presencial ou Online.', 'error'); return; }
      await createAppointment({ ...newAppt, clientPhone: newAppt.clientPhone.replace(/\D/g, ''), attendance: newAppt.attendance });
      // Removida a confirmação automática imediata para entrar como pendente
      // await updateAppointmentStatus(createdAppt.id, 'confirmed', 'Agendado manualmente pelo administrador.');

      setRefreshKey(p => p + 1);
      addToast("Agendamento criado com sucesso!", "success");
      setIsCreateModalOpen(false);
      setNewAppt({ clientName: '', clientPhone: '', date: '', time: '', notes: '', attendance: '' });
    } catch (err: any) {
      addToast(err.message || "Erro ao criar agendamento.", "error");
    } finally {
      setIsCreating(false);
    }
  };

  // --- DERIVED LISTS ---

  // 1. Pending: Sort by Appointment Date/Time ASC
  const pendingAppointments = appointments
    .filter(a => activeTab === 'requests' ? Boolean(a.rescheduleRequest) && a.status !== 'cancelled' : a.status === 'pending')
    .sort((a, b) => {
      const dateA = new Date(`${a.date}T${a.time}`);
      const dateB = new Date(`${b.date}T${b.time}`);
      return dateA.getTime() - dateB.getTime();
    });

  // 2. Upcoming 7 Days: Confirmed only
  const upcomingAppointments = appointments
    .filter(a => a.status === 'confirmed')
    .filter(a => {
      const apptDate = a.date;
      // Simple string comparison works for ISO dates (YYYY-MM-DD)
      if (apptDate < todayStr) return false;

      const sevenDaysLater = new Date();
      sevenDaysLater.setDate(sevenDaysLater.getDate() + 7);
      const sevenDaysLaterStr = sevenDaysLater.toISOString().split('T')[0];

      return apptDate >= todayStr && apptDate <= sevenDaysLaterStr;
    })
    .sort((a, b) => {
      const dateA = new Date(`${a.date}T${a.time}`);
      const dateB = new Date(`${b.date}T${b.time}`);
      return dateA.getTime() - dateB.getTime();
    });

  // 3. History: Filtered and Sorted
  const filteredHistory = appointments
    .filter(a => a.status !== 'pending')
    .filter(a => {
      const matchName = filterName ? a.clientName.toLowerCase().includes(filterName.toLowerCase()) : true;
      const matchDate = filterDate ? a.date === filterDate : true;
      const matchStatus = filterStatus === 'all' ? true : a.status === filterStatus;
      return matchName && matchDate && matchStatus;
    })
    .sort((a, b) => {
      const dateA = new Date(`${a.date}T${a.time}`);
      const dateB = new Date(`${b.date}T${b.time}`);
      return dateB.getTime() - dateA.getTime(); // DESC
    });

  // Pagination Logic
  const totalHistoryPages = Math.ceil(filteredHistory.length / itemsPerPage);
  const currentHistory = filteredHistory.slice(
    (historyPage - 1) * itemsPerPage,
    historyPage * itemsPerPage
  );

  // Reset page when filters change
  useEffect(() => {
    setHistoryPage(1);
  }, [filterName, filterDate]);


  // --- KPI CALCULATIONS ---
  const kpiTotalToday = appointments.filter(a => a.date === todayStr && a.status !== 'cancelled').length;
  const kpiTotalPending = appointments.filter(a => a.status === 'pending').length;
  const kpiTotalUpcoming = appointments.filter(a => {
    if (a.status !== 'confirmed') return false;
    const d = a.date;
    const nextWeek = new Date();
    nextWeek.setDate(nextWeek.getDate() + 7);
    const nextWeekStr = nextWeek.toISOString().split('T')[0];
    return d >= todayStr && d <= nextWeekStr;
  }).length;

  // --- CALENDAR EVENTS ---
  const calendarEvents = appointments
    .filter(a => a.status !== 'cancelled')
    .map(a => {
      // Create Date objects for start and end
      // Assuming 50 min duration (or 1 hour slot)
      const start = new Date(`${a.date}T${a.time}`);
      const end = new Date(start.getTime() + (config?.slotDurationMinutes || 50) * 60 * 1000);

      return {
        id: a.id,
        title: `${a.time} - ${a.clientName.split(' ')[0]} · ${attendanceLabel(a.attendance)}`,
        start,
        end,
        resource: a,
        status: a.status
      };
    });

  const eventStyleGetter = (event: any) => {
    const backgroundColor = event.status === 'pending' ? '#F5CD62' : '#31815B';

    return {
      style: {
        backgroundColor,
        borderRadius: '6px',
        opacity: 1,
        color: event.status === 'pending' ? '#493807' : 'white',
        border: '0px',
        display: 'block',
        fontSize: '0.75rem' // text-xs
      }
    };
  };

  if (loading && appointments.length === 0) {
    return (
      <div className="p-12 flex justify-center items-center">
        <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-primary"></div>
        <span className="ml-3 text-sm text-primary/60">Carregando...</span>
      </div>
    );
  }

  return (
    <div className="animate-fade-in space-y-6">
      {selectedAppointment && <AppointmentDetails appointment={selectedAppointment} onClose={() => setSelectedAppointment(null)} onRefresh={() => setRefreshKey(value => value + 1)} onConfirm={async () => requestConfirmation(selectedAppointment.id)} onCancel={() => { openCancelModal(selectedAppointment.id); setSelectedAppointment(null); }} />}
      {loadError && <div role="alert" className="bg-red-50 text-red-700 p-4 rounded-lg text-sm">Não foi possível carregar a agenda. <button onClick={() => setRefreshKey(value => value + 1)} className="underline font-semibold">Tentar novamente</button></div>}

      <AppointmentOverview today={kpiTotalToday} pending={kpiTotalPending} upcoming={kpiTotalUpcoming} requests={appointments.filter(item => item.rescheduleRequest && item.status !== 'cancelled').length} loading={loading} showFocus={false} sessions={appointments.filter(item => item.status !== 'cancelled' && new Date(item.date + 'T' + item.time).getTime() >= Date.now()).sort((a, b) => (a.date + a.time).localeCompare(b.date + b.time)).slice(0, 4)} onOpen={setSelectedAppointment} onNavigate={navigate} onRefresh={() => setRefreshKey(value => value + 1)} />
      {/* Internal Tabs and Action Button */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 border-b border-gray-200 gap-4 sm:gap-0">
        {!section ? <div className="flex gap-6 overflow-x-auto w-full sm:w-auto pb-1 sm:pb-0">
          <button
            onClick={() => setActiveTab('pending')}
            className={`pb-3 text-sm font-medium transition-all relative px-2 flex items-center gap-2 whitespace-nowrap
            ${activeTab === 'pending' ? 'text-primary' : 'text-gray-400 hover:text-primary/70'}`}
          >
            Pendentes
            {pendingAppointments.length > 0 && (
              <span className="bg-yellow-100 text-yellow-700 text-[10px] font-bold px-2 py-0.5 rounded-full shadow-sm border border-yellow-200">
                {pendingAppointments.length}
              </span>
            )}
            {activeTab === 'pending' && <div className="absolute bottom-0 left-0 w-full h-0.5 bg-accent"></div>}
          </button>

          <button
            onClick={() => setActiveTab('upcoming')}
            className={`pb-3 text-sm font-medium transition-all relative px-2 whitespace-nowrap
            ${activeTab === 'upcoming' ? 'text-primary' : 'text-gray-400 hover:text-primary/70'}`}
          >
            Próximos 7 Dias
            <span className="ml-2 bg-primary/10 text-primary text-[10px] font-bold px-1.5 py-0.5 rounded-full">
              {upcomingAppointments.length}
            </span>
            {activeTab === 'upcoming' && <div className="absolute bottom-0 left-0 w-full h-0.5 bg-accent"></div>}
          </button>

          <button
            onClick={() => setActiveTab('calendar')}
            className={`pb-3 text-sm font-medium transition-all relative px-2 whitespace-nowrap
            ${activeTab === 'calendar' ? 'text-primary' : 'text-gray-400 hover:text-primary/70'}`}
          >
            Calendário
            {activeTab === 'calendar' && <div className="absolute bottom-0 left-0 w-full h-0.5 bg-accent"></div>}
          </button>

          <button
            onClick={() => setActiveTab('history')}
            className={`pb-3 text-sm font-medium transition-all relative px-2 whitespace-nowrap
            ${activeTab === 'history' ? 'text-primary' : 'text-gray-400 hover:text-primary/70'}`}
          >
            Histórico / Filtros
            {activeTab === 'history' && <div className="absolute bottom-0 left-0 w-full h-0.5 bg-accent"></div>}
          </button>
        </div> : <h3 className="text-sm font-medium text-primary">{section === 'calendar' ? 'Agenda por dia, semana ou mês' : section === 'requests' ? 'Pedidos de remarcação' : section === 'pending' ? 'Solicitações aguardando confirmação' : section === 'upcoming' ? 'Sessões dos próximos 7 dias' : 'Consultar agendamentos anteriores'}</h3>}

        <div className="pb-3 sm:pb-2">
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="px-4 py-2 bg-primary text-white text-xs font-semibold rounded-md shadow hover:bg-primary-hover transition-colors flex items-center gap-2"
          >
            <span>+</span> Novo Agendamento
          </button>
        </div>
      </div>

      {(activeTab === 'pending' || activeTab === 'requests') && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          {pendingAppointments.length === 0 ? (
            <div className="p-12 text-center flex flex-col items-center justify-center h-full">
              <div className="text-4xl mb-4 opacity-50">✅</div>
              <h3 className="text-lg font-serif text-primary mb-1">Tudo em dia!</h3>
              <p className="text-xs text-gray-400">{activeTab === 'requests' ? 'Nenhum pedido de remarcação pendente.' : 'Sem confirmações pendentes.'}</p>
            </div>
          ) : (
            <AppointmentList appointments={pendingAppointments} onView={setSelectedAppointment} onConfirm={requestConfirmation} onCancel={openCancelModal} busyId={confirmingId} />
          )
          }
        </div>
      )
      }



      {
        activeTab === 'upcoming' && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            {upcomingAppointments.length === 0 ? (
              <div className="p-12 text-center flex flex-col items-center justify-center h-full">
                <h3 className="text-lg font-serif text-primary mb-1">Agenda Livre</h3>
                <p className="text-xs text-gray-400">Nenhum agendamento confirmado para os próximos 7 dias.</p>
              </div>
            ) : (
              <AppointmentList appointments={upcomingAppointments} onView={setSelectedAppointment} onConfirm={requestConfirmation} onCancel={openCancelModal} busyId={confirmingId} />
            )
            }
          </div >
        )
      }


      {activeTab === 'calendar' && (
        <div className="space-y-3"><div className="flex flex-wrap gap-4 text-xs text-gray-600" aria-label="Legenda do calendário"><span className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full bg-[#31815B]" />Confirmada</span><span className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full bg-[#F5CD62]" />Pendente de confirmação</span></div><div className="admin-calendar bg-white rounded-2xl shadow-sm border border-primary/10 overflow-hidden h-[660px] p-3 md:p-5 text-sm mb-8">
          <Calendar
            localizer={localizer}
            events={calendarEvents}
            startAccessor="start"
            endAccessor="end"
            scrollToTime={new Date(1970, 0, 1, Number((config?.startHour || '08:00').split(':')[0]), Number((config?.startHour || '08:00').split(':')[1]))}
            style={{ height: '100%' }}
            messages={{
              next: "Próximo",
              previous: "Anterior",
              today: "Hoje",
              month: "Mês",
              week: "Semana",
              day: "Dia",
              agenda: "Agenda",
              date: "Data",
              time: "Hora",
              event: "Evento"
            }}
            eventPropGetter={eventStyleGetter}
            views={[Views.MONTH, Views.WEEK, Views.DAY]}
            view={calendarView}
            onView={setCalendarView}
            date={calendarDate}
            onNavigate={setCalendarDate}
            onSelectEvent={(event) => {
              setSelectedAppointment(event.resource);
            }}
          />
        </div></div>
      )}

      {activeTab === 'calendar' && <AppointmentOverview today={kpiTotalToday} pending={kpiTotalPending} upcoming={kpiTotalUpcoming} requests={appointments.filter(item => item.rescheduleRequest && item.status !== 'cancelled').length} loading={loading} showFocus={true} focusOnly sessions={appointments.filter(item => item.status !== 'cancelled' && new Date(item.date + 'T' + item.time).getTime() >= Date.now()).sort((a, b) => (a.date + a.time).localeCompare(b.date + b.time)).slice(0, 4)} onOpen={setSelectedAppointment} onNavigate={navigate} onRefresh={() => setRefreshKey(value => value + 1)} />}

      {
        activeTab === 'history' && (
          <div className="space-y-4">

            {/* Filter Bar */}
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100 flex flex-col md:flex-row gap-3 items-end">
              <div className="flex-1 w-full">
                <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Buscar por Nome</label>
                <input
                  type="text"
                  placeholder="Nome do paciente..."
                  value={filterName}
                  onChange={(e) => setFilterName(e.target.value)}
                  className="w-full border border-gray-200 rounded p-2 text-sm focus:ring-1 focus:ring-accent focus:border-accent outline-none text-primary"
                />
              </div>
              <div className="w-full md:w-40">
                <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Filtrar por Data</label>
                <input
                  type="date"
                  value={filterDate}
                  onChange={(e) => setFilterDate(e.target.value)}
                  className="w-full border border-gray-200 rounded p-2 text-sm focus:ring-1 focus:ring-accent focus:border-accent outline-none text-primary"
                />
              </div>
              <div className="w-full md:w-32">
                <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Status</label>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value as 'all' | 'confirmed' | 'cancelled')}
                  className="w-full border border-gray-200 rounded p-2 text-sm focus:ring-1 focus:ring-accent focus:border-accent outline-none text-primary"
                >
                  <option value="all">Todos</option>
                  <option value="confirmed">Confirmados</option>
                  <option value="cancelled">Cancelados</option>
                </select>
              </div>
              {(filterName || filterDate || filterStatus !== 'all') && (
                <button
                  onClick={() => { setFilterName(''); setFilterDate(''); setFilterStatus('all'); }}
                  className="px-3 py-2 text-xs text-red-500 hover:bg-red-50 rounded border border-transparent hover:border-red-100 transition-colors whitespace-nowrap"
                >
                  Limpar
                </button>
              )}
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              <AppointmentList appointments={currentHistory} onView={setSelectedAppointment} onConfirm={requestConfirmation} onCancel={openCancelModal} busyId={confirmingId} />

              {/* Pagination */}
              {totalHistoryPages > 1 && (
                <div className="p-3 border-t border-gray-100 flex justify-center items-center gap-2">
                  <button
                    disabled={historyPage === 1}
                    onClick={() => setHistoryPage(p => p - 1)}
                    className="px-3 py-1 border border-gray-200 rounded disabled:opacity-30 hover:bg-gray-50 text-xs font-medium transition-colors"
                  >
                    Anterior
                  </button>
                  <span className="text-xs text-gray-500 font-medium">
                    {historyPage} / {totalHistoryPages}
                  </span>
                  <button
                    disabled={historyPage === totalHistoryPages}
                    onClick={() => setHistoryPage(p => p + 1)}
                    className="px-3 py-1 border border-gray-200 rounded disabled:opacity-30 hover:bg-gray-50 text-xs font-medium transition-colors"
                  >
                    Próxima
                  </button>
                </div>
              )
              }
            </div>
          </div>
        )
      }

      {appointmentToConfirm && <Modal title="Confirmar esta sessão?" subtitle="Confira os dados antes de aprovar o agendamento." onClose={() => setAppointmentToConfirm(null)} busy={Boolean(confirmingId)} footer={<><Button variant="secondary" disabled={Boolean(confirmingId)} onClick={() => setAppointmentToConfirm(null)}>Voltar</Button><Button disabled={Boolean(confirmingId)} onClick={submitConfirmation}>{confirmingId ? 'Confirmando...' : 'Sim, confirmar'}</Button></>}>
        <div className="session-info-card"><span className="modal-field-label">Paciente</span><strong className="text-primary">{appointmentToConfirm.clientName}</strong><p>{formatDateBr(appointmentToConfirm.date)} às {appointmentToConfirm.time} · {attendanceLabel(appointmentToConfirm.attendance)}</p></div>
        <p className="text-sm text-gray-500 mt-4">Ao confirmar, esta sessão passará de pendente para confirmada.</p>
        {confirmError && <p role="alert" className="text-sm text-red-700 mt-4">{confirmError}</p>}
      </Modal>}
      {isCancelModalOpen && <Modal title={appointments.find(item => item.id === appointmentToCancel)?.status === 'pending' ? 'Rejeitar solicitação' : 'Cancelar sessão'} onClose={() => setIsCancelModalOpen(false)} busy={isCancelling} footer={<><Button variant="secondary" disabled={isCancelling} onClick={() => setIsCancelModalOpen(false)}>Voltar</Button><Button variant="danger" disabled={isCancelling} onClick={handleCancelSubmit}>{isCancelling ? 'Salvando...' : 'Confirmar cancelamento'}</Button></>}>
        <div className="session-info-card mb-4"><strong>{appointments.find(item => item.id === appointmentToCancel)?.clientName}</strong><p>O horário será liberado após o cancelamento.</p></div>
        <label htmlFor="cancel-reason" className="modal-field-label">Motivo do cancelamento <span>(opcional)</span></label><textarea id="cancel-reason" rows={3} placeholder="Informe o motivo..." value={cancelReason} onChange={event => setCancelReason(event.target.value)} />
      </Modal>}

      {/* Create Modal */}
      {isCreateModalOpen && <Modal title="Novo agendamento" subtitle="Preencha os dados do paciente e da sessão." busy={isCreating} onClose={() => setIsCreateModalOpen(false)} footer={<><Button variant="secondary" disabled={isCreating} onClick={() => setIsCreateModalOpen(false)}>Voltar</Button><Button type="submit" form="create-appointment" disabled={isCreating}>{isCreating ? 'Salvando...' : 'Criar agendamento'}</Button></>}>
              <form id="create-appointment" onSubmit={handleCreateSubmit} className="space-y-4 text-sm">
                <div>
                  <label htmlFor="create-clientName" className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">
                    Nome do Paciente *
                  </label>
                  <input id="create-clientName"
                    type="text"
                    required
                    value={newAppt.clientName}
                    onChange={(e) => setNewAppt({ ...newAppt, clientName: e.target.value })}
                    className="w-full border border-gray-200 rounded p-2 focus:ring-1 focus:ring-accent focus:border-accent outline-none text-primary"
                    placeholder="Nome completo"
                  />
                </div>

                <div>
                  <label htmlFor="create-clientPhone" className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">
                    WhatsApp *
                  </label>
                  <input id="create-clientPhone"
                    type="tel"
                    required
                    value={newAppt.clientPhone}
                    onChange={(e) => {
                      const val = maskPhone(e.target.value);
                      setNewAppt({ ...newAppt, clientPhone: val });
                    }}
                    className="w-full border border-gray-200 rounded p-2 focus:ring-1 focus:ring-accent focus:border-accent outline-none text-primary"
                    placeholder="(11) 99999-9999" maxLength={15} autoComplete="tel-national" inputMode="tel"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="create-date" className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">
                      Data *
                    </label>
                    <input id="create-date"
                      type="date"
                      required
                      value={newAppt.date}
                      onChange={(e) => setNewAppt({ ...newAppt, date: e.target.value, attendance: '', time: '' })}
                      className="w-full border border-gray-200 rounded p-2 focus:ring-1 focus:ring-accent focus:border-accent outline-none text-primary"
                    />
                  </div>
                  <div>
                    <label htmlFor="create-time" className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">
                      Horário *
                    </label>
                    <select id="create-time"
                      required
                      value={newAppt.time}
                      onChange={(e) => setNewAppt({ ...newAppt, time: e.target.value })}
                      className="w-full border border-gray-200 rounded p-2 focus:ring-1 focus:ring-accent focus:border-accent outline-none text-primary"
                    ><option value="">Selecione</option>{config && newAppt.attendance && generateTimeSlots(newAppt.date, config, appointments).map(slot => <option key={slot.time} value={slot.time}>{slot.time}</option>)}</select>
                  </div>
                </div>

                {newAppt.date && config && <div><label htmlFor="create-attendance" className="block text-xs font-medium mb-2">Tipo de atendimento *</label><select id="create-attendance" required value={newAppt.attendance} onChange={e => setNewAppt({ ...newAppt, attendance: e.target.value as Attendance, time: '' })} className="w-full border rounded p-2"><option value="">Selecione</option>{getAvailableAttendance(newAppt.date, config).map(value => <option key={value} value={value}>{attendanceLabel(value)}</option>)}</select></div>}

                <div>
                  <label htmlFor="create-notes" className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">
                    Observações (Opcional)
                  </label>
                  <textarea id="create-notes"
                    value={newAppt.notes}
                    onChange={(e) => setNewAppt({ ...newAppt, notes: e.target.value })}
                    className="w-full border border-gray-200 rounded p-2 focus:ring-1 focus:ring-accent focus:border-accent outline-none resize-none"
                    rows={2}
                    placeholder="Informações adicionais..."
                  />
                </div>

              </form>
      </Modal>}
    </div>
  );
};
